var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Suspense, useState } from 'react';
import { observer } from 'mobx-react';
import Icon from '@ant-design/icons';
import { Dropdown } from 'antd';
import classnames from 'classnames';
import { isRegularMessage, MessagingChannelFeature, Permissions, isSpecialMessage, isMediaMessage, MEDIA_TYPE } from '@whatsper/texterchat-common';
import { useTranslation } from 'react-i18next';
import lazyWithRetry from '../../../../../helpers/lazyWithRetry';
import { withStore } from '../../../../../store/rootStore';
import { ReactComponent as DownArrowWhite } from '../../../../../assets/icons/arrows/downArrowWhite.svg';
import { ReactComponent as DownArrowBlack } from '../../../../../assets/icons/arrows/downArrowBlack.svg';
import { GoReply } from 'react-icons/go';
import { BsForward, BsPencil } from 'react-icons/bs';
import { AiOutlineRobot } from 'react-icons/ai';
import { MdOutlineFileDownload } from 'react-icons/md';
import Trashbin from '../../../../icons/trashbin';
import { getDirection } from '../../../../../helpers';
import { getFileUrl } from '../../../../../store/FilesStore';
import './MessageContextMenu.scss';
const BotEditorModal = lazyWithRetry(() => import('../../../BotEditorModal'));
const MessageContextMenu = ({ message, incoming, handleForwardMessage, store, messageId, }) => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const [botMenuOpen, setBotMenuOpen] = useState(false);
    const botMsgInfo = isRegularMessage(message)
        ? message.botMsgInfo || ((_a = message.metadata) === null || _a === void 0 ? void 0 : _a.botMsgInfo)
        : null;
    const { sessionIsOver, chat } = store.activeChatStore;
    const isAssignedAgent = (chat === null || chat === void 0 ? void 0 : chat.agent.uid) === ((_b = store.loginStore.userDetails) === null || _b === void 0 ? void 0 : _b._id);
    const audioMedia = isMediaMessage(message)
        ? message.media.find((m) => m.mediaType === MEDIA_TYPE.AUDIO)
        : undefined;
    const downloadAudio = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!audioMedia)
            return;
        let downloadUrl;
        if (audioMedia.fileId) {
            downloadUrl = window.ReactNativeWebView
                ? yield store.files.shareFile(audioMedia.fileId)
                : getFileUrl(audioMedia.fileId, true, audioMedia.filename);
        }
        else {
            downloadUrl = audioMedia.url;
        }
        if (!downloadUrl) {
            console.error('Audio message has no file ID or URL');
            return;
        }
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'openURL', url: downloadUrl }));
        }
        else {
            window.open(downloadUrl, '_blank');
        }
    });
    const supportsDelete = () => {
        const adapter = (chat === null || chat === void 0 ? void 0 : chat.channelInfo) ? store.channels.getAccountAdapter({
            id: chat.channelInfo.accountId,
            name: chat.channelInfo.name,
        }) : null;
        return store.channels.hasFeatures([MessagingChannelFeature.deleteMessage], adapter);
    };
    const handleOpenBot = () => {
        setBotMenuOpen(!botMenuOpen);
    };
    const deleteMessage = () => {
        store.messages.deleteMessage(messageId);
    };
    const canForward = isRegularMessage(message)
        && (
        // Maybe later we will implement some API to decide if the message can be forwarded,
        // but for now safer to disallow forwarding of special messages
        isSpecialMessage(message)
            // We don't want to allow forwarding of status messages in any case
            || !((_c = message.special) === null || _c === void 0 ? void 0 : _c.isStatusMessage));
    const menu = () => {
        const { setReplyMessage } = store.activeChatStore;
        const { hasPermissions } = store.agentsStore;
        const { setSelectedMessageId } = store.chatNotesStore;
        const items = [
            ...(!sessionIsOver
                ? [
                    {
                        key: 'replyMessage',
                        label: t('replyMessage'),
                        onClick: () => setReplyMessage(message),
                        icon: React.createElement(GoReply, null),
                    },
                ]
                : []),
            ...(incoming && supportsDelete()
                ? [
                    {
                        key: 'deleteMessage',
                        label: t('deleteMessage'),
                        onClick: deleteMessage,
                        icon: React.createElement(Trashbin, { color: "#EB584E" })
                    },
                ]
                : []),
            ...canForward
                ? [{
                        key: 'forwardMessage',
                        label: t('forwardMessage'),
                        onClick: handleForwardMessage,
                        icon: React.createElement(BsForward, null),
                    }]
                : [],
            {
                key: 'addNote',
                label: t('addNote'),
                onClick: () => setSelectedMessageId(messageId),
                icon: React.createElement(BsPencil, null),
            },
            ...(audioMedia
                ? [
                    {
                        key: 'downloadAudio',
                        label: t('download'),
                        onClick: downloadAudio,
                        icon: React.createElement(MdOutlineFileDownload, null),
                    },
                ]
                : []),
            ...(hasPermissions([Permissions.Permission.bots_edit]) && botMsgInfo
                ? [
                    {
                        key: 'editBot',
                        label: t('editBot'),
                        onClick: handleOpenBot,
                        icon: React.createElement(AiOutlineRobot, null),
                    },
                ]
                : []),
        ];
        return Object.assign({ items }, getDirection() === 'rtl' && { style: { direction: 'rtl' } });
    };
    return (React.createElement(React.Fragment, null,
        isAssignedAgent && (React.createElement(Dropdown, { trigger: ['click'], menu: menu(), placement: getDirection() === 'rtl' ? 'bottomLeft' : 'bottomRight', overlayClassName: 'context-menu-overlay-wrapper' },
            React.createElement("div", { className: classnames('context-menu-trigger-wrapper', {
                    'context-menu-incoming': incoming,
                    'with-background': !incoming,
                }) },
                React.createElement(Icon, { component: incoming ? DownArrowBlack : DownArrowWhite })))),
        botMsgInfo && (React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
            React.createElement(BotEditorModal, { openModal: botMenuOpen, setOpenModal: handleOpenBot, botMsgInfo: botMsgInfo })))));
};
export default withStore(observer(MessageContextMenu));
